import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PhoneOtpLogin from "@/components/PhoneOtpLogin";
import { ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalSignup = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const inviteToken = sp.get("invite") || "";
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inviteToken) { setStatus("invalid"); setError("Missing invite token."); return; }
    axios.get(`${API}/portal/invite/verify?token=${encodeURIComponent(inviteToken)}`)
      .then(({ data }) => { setInvite(data); setStatus("valid"); })
      .catch((e) => { setError(e?.response?.data?.detail || "This invite is invalid or has expired."); setStatus("invalid"); });
  }, [inviteToken]);

  const onOtpSuccess = async ({ access_token }) => {
    localStorage.setItem("ca_parent_token", access_token);
    try {
      const { data } = await axios.post(`${API}/portal/invite/redeem`, {
        invite_token: inviteToken,
        parent_access_token: access_token,
      });
      toast.success("Invite redeemed — welcome!");
      setStatus("done");
      // small delay so success state is visible
      setTimeout(() => nav("/portal"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem invite");
      nav("/portal");
    }
  };

  return (
    <main data-testid="portal-signup" className="min-h-screen flex flex-col bg-background">
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={14} /> Home
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 md:p-10">
          {status === "checking" && (
            <div className="text-muted-foreground text-sm">Verifying your invite…</div>
          )}
          {status === "invalid" && (
            <div data-testid="invite-invalid">
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Invite unavailable</div>
              <h1 className="mt-3 font-serif-display text-2xl">Sorry — {error}</h1>
              <p className="mt-3 text-sm text-muted-foreground">Please ask your host to send you a new QR code or link.</p>
              <Link to="/portal/login" className="mt-6 inline-block text-primary text-sm hover:underline">Sign in to an existing account →</Link>
            </div>
          )}
          {status === "valid" && invite && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-primary/80">You're invited to</div>
                  <div className="font-serif-display text-2xl">{invite.event?.name}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Sign in with your mobile number to complete your child's care profile before the event. We'll text you a one-time code — no password to remember.
              </p>
              <PhoneOtpLogin purpose="parent" onSuccess={onOtpSuccess} allowSignup />
            </div>
          )}
          {status === "done" && (
            <div data-testid="invite-done" className="flex flex-col items-center gap-3 text-center py-4">
              <CheckCircle2 size={36} className="text-primary" />
              <div className="font-serif-display text-2xl">You're in.</div>
              <div className="text-sm text-muted-foreground">Redirecting to your portal…</div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default PortalSignup;
